import * as React from 'react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';

export type IccoReportType = 'IOB' | 'CPOB' | 'CPOBPP' | 'ICPOB' | 'CS' | 'IBOC' | 'INVCOMP' | 'IOS';

export interface IIccoExcelReportParams {
    reportType: IccoReportType;
    office: string;
    filename: string;
}

interface IIccoExcelReportDialogProps {
    dialogName: string;
    hideDialog: () => void;
    onGenerateReport: (params: IIccoExcelReportParams) => void;
    message?: string;
    generating?: boolean;
}

// Matches the real screen's own Select Type poplist labels (confirmed via the .fmt's own string
// table, not guessed) - value codes match :excel.report's literal LOV values.
const REPORT_TYPE_OPTIONS: Array<{ value: IccoReportType; label: string }> = [
    { value: 'IOB', label: 'Invoice Office Breakdown' },
    { value: 'CPOB', label: 'Contractor Pay Office Breakdown' },
    { value: 'CPOBPP', label: 'Contractor Pay Office Breakdown - Pay Period' },
    { value: 'ICPOB', label: 'Invoice-Contractor Pay Office Breakdown' },
    { value: 'CS', label: 'Contractor Summary' },
    { value: 'IBOC', label: 'Invoice Breakdown by Office and Client' },
    { value: 'INVCOMP', label: 'Invoice Comparison' },
    { value: 'IOS', label: 'Invoice State Breakdown' },
];

// Only IOS/CS/INVCOMP's stored procs actually read an @Office value (confirmed per-report via
// its own deployed RDL/proc) - the other 5 report types ignore it entirely, so the field is only
// shown for these.
const REPORT_TYPES_WITH_OFFICE: ReadonlyArray<IccoReportType> = ['IOS', 'CS', 'INVCOMP'];

// Matches oid 76's own v_filename defaults per :excel.report value.
const DEFAULT_FILENAMES: Record<IccoReportType, string> = {
    IOB: 'Invoice_Office_Breakdown.xlsx',
    CPOB: 'Contract_Pay_Office_Breakdown.xlsx',
    CPOBPP: 'Contract-Pay-Office-Breakdown-By-PP.xlsx',
    ICPOB: 'Invoice-Contract_Pay_Office_Breakdown.xlsx',
    CS: 'Contractor_Summary.xlsx',
    IBOC: 'Invoice_Breakdown_Office_Client.xlsx',
    INVCOMP: 'Invoice_Comparison.xlsx',
    IOS: 'Invoice_State_Breakdown.xlsx',
};

// Mirrors the EXCEL block/canvas (window "Excel") - Select Type/Office/Filename/OK/Cancel.
// RPT_DATE is deliberately dropped - traced every use of it across the whole .fmt (it's threaded
// through 4-5 levels of Excel_*/Excel_*_Data procedure calls but never actually read anywhere;
// even the report header's own "Date" stamp uses sysdate directly, not :excel.rpt_date). The
// Filename/browse-button pair mirrors every other converted report dialog in this app - the
// browse-to-a-local-save-path button has no browser equivalent, so Filename is just a plain,
// user-editable text field defaulting per report type (oid 76's own v_filename literals), not a
// real file-system picker.
interface IIccoExcelReportFormValues {
    reportType: IccoReportType;
    office: string;
    filename: string;
}

const IccoExcelReportDialog = (props: IIccoExcelReportDialogProps) => {
    const { register, errors, setValue, watch, getValues } = useForm<IIccoExcelReportFormValues>({ defaultValues: { reportType: 'IOB', filename: DEFAULT_FILENAMES.IOB } });
    const reportType = watch('reportType') as IccoReportType;

    useEffect(() => {
        setValue('filename', DEFAULT_FILENAMES[reportType] ?? '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportType]);

    const handleOk = () => {
        const values = getValues();
        props.onGenerateReport({
            reportType: values.reportType as IccoReportType,
            office: values.office ?? '',
            filename: values.filename || DEFAULT_FILENAMES[values.reportType as IccoReportType],
        });
    };

    return (
        <Dialog title="Invoice/Contractor Pay Office Breakdown - Excel Report" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '32%' }}>
            <form>
                <RHFSelect
                    label="Select Type"
                    labelOnLeft={true}
                    labelStyle={{ whiteSpace: 'nowrap', width: '7rem' }}
                    name="reportType"
                    options={REPORT_TYPE_OPTIONS}
                    defaultVal="IOB"
                    register={register}
                    style={{ width: '100%' }}
                />
                {REPORT_TYPES_WITH_OFFICE.includes(reportType) && (
                    <div style={{ marginTop: '.75rem' }}>
                        <RHFInputBox
                            label="Office"
                            labelOnLeft={true}
                            labelStyle={{ whiteSpace: 'nowrap', width: '7rem' }}
                            name="office"
                            register={register}
                            errors={errors}
                            style={{ width: '100%' }}
                        />
                    </div>
                )}
                <div style={{ marginTop: '.75rem' }}>
                    <RHFInputBox
                        label="Filename"
                        labelOnLeft={true}
                        labelStyle={{ whiteSpace: 'nowrap', width: '7rem' }}
                        name="filename"
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                    />
                </div>
            </form>
            <div className="form-row" style={{ marginTop: '1.5rem', justifyContent: 'center' }}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '25%', marginRight: '1rem' }}
                    text={props.generating ? 'Generating...' : 'OK'}
                    disabled={props.generating}
                    loading={props.generating}
                    onClick={handleOk}
                />
                <Button className="blue-hover-button" style={{ width: '25%' }} text="Cancel" disabled={props.generating} onClick={() => props.hideDialog()} />
            </div>
            {!!props.message && (
                <div style={{ marginTop: '1rem', minHeight: '1.2rem', textAlign: 'center', color: 'red' }}>{props.message}</div>
            )}
        </Dialog>
    );
};

export default IccoExcelReportDialog;



// WEBPACK FOOTER //
// ./src/components/accounts-receivable/dialogs/IccoExcelReportDialog.tsx